import React from 'react';
import { makeStyles } from '@material-ui/core';
import { Paper } from '@mui/material';
import Grid from '@material-ui/core/Grid';
import { Page, Content } from "@backstage/core-components";
import { Card, CardBody, Link } from "@backstage/ui";
import { useApi, configApiRef } from "@backstage/core-plugin-api";
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';

import { pagesTranslationRef } from '../../translations/pages';
import LinkArrow, { LinkArrowHoverStyle } from '../LinkArrow';

import bar from '../assets/logos/bar.png';
import bar_en from '../assets/logos/bar_en.png';
import pg from '../assets/logos/pg.svg';
import pg_en from '../assets/logos/pg_en.svg';
import citask from '../assets/logos/citask.svg';
import citask_en from '../assets/logos/citask_en.svg';
import experiments_background from '../assets/experiments-base.jpg';
import development_background from '../assets/development-base.jpg';
import services_background from '../assets/services-base.jpg';


const useStyles = makeStyles(() => ({
    kpoParent: {
        padding: '0 0 6rem 0 !important',
        position: 'relative',
    },
    resourceLink: {
        display: 'block',
    },
    resourcePaper: {
        fontWeight: 500,
        fontSize: '1.125rem',
        color: '#0e151d',
        backgroundColor: '#f7f8f9',
        boxShadow: 'none',
        borderRadius: '1rem',
        padding: '1rem 1rem 0.75rem 1rem',
        display: 'flex',
        flexDirection: 'column',
        rowGap: '0.5rem',
        transition: 'background-color 300ms',
        '& div:first-child': {
            textAlign: 'right',
            height: '1.25rem',
        },
        '&:hover svg': LinkArrowHoverStyle,
        '&:hover': {
            backgroundColor: 'white',
        },
    },
    header: {
        margin: 0,
        padding: 0,
        gridArea: 'pageHeader',
    },
    headerCaption: {
        fontWeight: 500,
        marginBottom: '0.5rem',
        backgroundImage: 'linear-gradient(27deg,#2353a5 4.5%,#0cabf3 16.7%,#0fa0e9 35%,#2353a5 42.5%)',  // halved due to full-screen width of the header
        color: 'transparent',
        backgroundClip: 'text',
        letterSpacing: '-0.05em',
        fontSize: 'clamp(2.25rem,4.1vw,3.125rem)',
        margin: '0',
        padding: '0',
    },
    headerContent: {
        marginTop: '0.5rem',
        fontSize: 'clamp(0.625rem, 1.0vw, 1rem)',
        color: 'black',
        maxWidth: '40rem',
    },
    subsectionHeader: {
        fontSize: 'clamp(1.25rem, 1.2vw, 1.5rem)',
        fontWeight: 500,
        margin: '1.5rem 0 0 0',
        padding: 0,
        backgroundImage: 'linear-gradient(27deg,#2353a5 4.5%,#0cabf3 16.7%,#0fa0e9 35%,#2353a5 42.5%)',  // halved due to full-screen width of the header
        color: 'transparent',
        backgroundClip: 'text',
        letterSpacing: '-0.05em',
    },
    logosBox: {
        display: 'flex',
        flexFlow: 'row',
        justifyContent: 'space-around',
        boxSizing: 'border-box',
        height: '5rem',
        textAlign: 'right',
        position: 'absolute',
        margin: '0',
        padding: '0.625rem 0 0 0',
        right: '0.75rem',
        bottom: '0.5rem',
        width: 'min(calc(100% - 1.5rem), calc(100vw - 1.5rem))',
        maxWidth: 'min(calc(100% - 1.5rem), calc(100vw - 1.5rem))',
        overflowX: 'auto',
        overflowY: 'hidden',
        borderTop: '1px solid color(srgb 0.392157 0.454902 0.545098 / 0.2)',
    },
    kpo: {
        flex: 0,
    },
    kpoLogo: {
        boxSizing: 'border-box',
        height: '100%',
        minHeight: '11.4mm',
        margin: 0,
        padding: 0,
        backgroundColor: 'white',
    },
    logos: {
        display: 'flex',
        flexFlow: 'row',
        minHeight: '4rem',
        flex: 0,
    },
    separator: {
        width: '0px',
        borderLeft: '0.125rem solid color(srgb 0.392157 0.454902 0.545098 / 0.2)',
        height: '2.7rem',
        margin: '0.9rem 2.7rem',
    },
    pgLogo: {
        height: '2.7rem',
        minWidth: '16.75mm',
        marginTop: '0.9rem',
        marginRight: '0.25rem',
    },
    citaskLogo: {
        marginTop: '0.9rem',
        height: '2.7rem',
        padding: '0.25rem 0.25rem 0.25rem 0.375rem',
        verticalAlign: 'top',
        borderLeft: '0.125rem solid #004c88',
    },
    areaCard: {
        display: 'flex',
        backgroundRepeat: 'no-repeat',
        padding: '1.25rem',
        minHeight: '22rem',
        height: '45vh',
        maxHeight: '31.25rem',
        borderRadius: '1.25rem',
        backgroundSize: '200%',
        transition: 'background-size .5s',
        backgroundPosition: 'center',
        overflow: 'hidden',
        '&:hover': {
            backgroundSize: '250%',
        },
    },
    cardLink: {
        display: 'block',
        '&:hover svg': {
            transform: 'translate(0, 0.25rem) rotate(45deg)',
        },
    },
    areaBody: {
        color: 'white',
        display: 'flex',
        rowGap: '1rem',
        flexFlow: 'column',
        justifyContent: 'end',
        fontSize: 'clamp(0.625rem, 1.0vw, 1rem)',
        margin: '0',
        padding: '0',
        overflow: 'hidden',
        '& h2': {
            letterSpacing: '-0.05rem',
            fontSize: 'clamp(1.25rem, 2.1vw, 2rem)',
            margin: '0',
            padding: '0',
            fontWeight: 500,
        },
        '& div:nth-child(2)': {
            minHeight: '3rem',
        },
    },
    pseudolink: {
        color: 'rgb(12, 171, 243)',
        fontWeight: 500,
        fontSize: 'clamp(1rem, 1.2vw, 1.125rem)',
        '& svg': {
            width: '1.125rem !important',
            height: '1.125rem !important',
            transform: 'translate(0, 0.25rem)',
            marginRight: '0.25rem',
        },
    },
    experiments: {
        backgroundImage: `linear-gradient(180deg,transparent 44%,hsl(from #0e151d calc(h + 0) calc(s + 0) calc(l + 0) / 0.8) 74% 100%),url(${experiments_background})`,
    },
    development: {
        backgroundImage: `linear-gradient(180deg,transparent 44%,hsl(from #0e151d calc(h + 0) calc(s + 0) calc(l + 0) / 0.8) 74% 100%),url(${development_background})`,
    },
    services: {
        backgroundImage: `linear-gradient(180deg,transparent 44%,hsl(from #0e151d calc(h + 0) calc(s + 0) calc(l + 0) / 0.8) 74% 100%),url(${services_background})`,
    },
}));


type AreaBackground = 'experiments' | 'development' | 'services';


const PseudoLink = ({children}: {children: React.ReactNode}) => {
    const classes = useStyles();
    return (
        <span className={classes.pseudolink}>
            <LinkArrow fill='rgb(12, 171, 243)' stroke='black' />
            {children}
        </span>
    );
}


const ResourceGridItem = ({title, link}: {title: string, link: string}) => {
    const classes = useStyles();
    return (
        <Grid item xs={12} md={4}>
            <a href={link} target='_blank' className={classes.resourceLink}>
                <Paper className={classes.resourcePaper}>
                    <div><LinkArrow/></div>
                    <div>{title}</div>
                </Paper>
            </a>
        </Grid>
    );
}


const AreaGridItem = ({
    title,
    linkTitle,
    link,
    children,
    background,
}: {
    title: string,
    link: string,
    linkTitle: string,
    background: AreaBackground,
    children: React.ReactNode,
}) => {
    const classes = useStyles();
    return (
        <Grid item xs={12} md={4}>
            <Link href={link} className={classes.cardLink}>
                <Card className={`${classes.areaCard} ${classes[background]}`}>
                    <CardBody className={classes.areaBody}>
                        <h2>{title}</h2>
                        <div>{children}</div>
                        <PseudoLink>{linkTitle}</PseudoLink>
                    </CardBody>
                </Card>
            </Link>
        </Grid>
    );
}


const LogosSection = () => {
    const classes = useStyles();
    const {t} = useTranslationRef(pagesTranslationRef);
    const keySuffix = t('pages.keySuffix') as string;
    return (
        <div className={classes.logosBox}>
            <div className={classes.kpo}>
                <img src={keySuffix === '' ? bar : bar_en} alt={t('pages.home.altKPO') + ', ' + t('pages.home.altPL') + ', ' + t('pages.home.altEU')} className={classes.kpoLogo} />
            </div>
            <div className={classes.separator} />
            <div className={classes.logos}>
                <img src={keySuffix === '' ? pg : pg_en} alt={t('pages.home.altPG')} className={classes.pgLogo} />
                <img src={keySuffix === '' ? citask : citask_en} alt={t('pages.home.altTASK')} className={classes.citaskLogo} />
            </div>
        </div>
    )
}

export const HomePage = () => {
    const classes = useStyles();
    const configApi = useApi(configApiRef);
    const {t} = useTranslationRef(pagesTranslationRef);
    const keySuffix = t('pages.keySuffix') as string;

    const items = [];
    for(const cfg of configApi.getConfigArray('homeResources')) {
        const title = cfg.getString('title' + keySuffix);
        const url = cfg.getString('url');
        items.push({title, url});
    }

    const experimentsText = configApi.getString('areaExperiments.text' + keySuffix);
    const developmentText = configApi.getString('areaDevelopment.text' + keySuffix);
    const servicesText = configApi.getString('areaServices.text' + keySuffix);

    return (
        <Page themeId="home">
            <header className={classes.header}>
                <h1 className={classes.headerCaption}>{t('pages.home.title')}</h1>
                <p className={classes.headerContent}>{t('pages.home.headerContent')}</p>
            </header>
            <Content className={classes.kpoParent}>
                <Grid container spacing={3}>
                    <AreaGridItem title={t('pages.home.experiments')} link="experiments" linkTitle={t('pages.home.explore')} background="experiments">
                        {experimentsText}
                    </AreaGridItem>
                    <AreaGridItem title={t('pages.home.development')} link="development" linkTitle={t('pages.home.explore')} background="development">
                        {developmentText}
                    </AreaGridItem>
                    <AreaGridItem title={t('pages.home.services')} link="services" linkTitle={t('pages.home.explore')} background="services">
                        {servicesText}
                    </AreaGridItem>
                    <Grid item xs={12} md={12}>
                      <h2 className={classes.subsectionHeader}>{t('pages.home.moreResources')}</h2>
                    </Grid>
                    {items.map(({title, url}) => (
                        <ResourceGridItem title={title} link={url} />
                    ))}
                </Grid>
                <LogosSection />
            </Content>
        </Page>
    );
};
